import React, { useState, useEffect, useCallback, useRef, forwardRef } from 'react';

import { Skeleton } from '@material-ui/lab';
import { updateFeedTalkingPointCommentAction } from 'actions/feed';
import CommentContainer from 'components/Comment/CommentContainer';
import { usePagination } from 'components/hooks/usePagination';
import Post from 'models/post';
import InfiniteScroll from 'react-infinite-scroller';
import { useInView } from 'react-intersection-observer';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { StyledBox, StyledDivider, StyledTypography } from 'styles/common';

import { CHECK_IN_COMMENT_PLACEHOLDER, CHECK_IN_MEETING_NOTES_PLACEHOLDER } from 'constants/ProjectConstants';
import { convertDateToString2 } from 'constants/helper';
import theme from 'constants/theme';

import { getTalkingPointContinuousComments } from 'services/OneOnOne';
import { showErrorSnackbar } from 'services/snackbar';

import AutomatedComment from '../Comments/AutomatedComment';
import NoCommentsView from '../Comments/NoCommentsView';
import SidepanelCommentDisplay from '../Comments/SidepanelCommentDisplay';

const LIMIT = 10;
const UP_LIMIT = 10;

export const SCROLL_DIRECTION = {
  UPWARD: 'UPWARD',
  DOWNWARD: 'DOWNWARD',
  ACCORDION: 'ACCORDION',
};

const getDividerObj = (instanceId, dueTS) => ({
  instanceId,
  dueTS,
  comment: null,
});

const addDateDividers = (commentObjsArray, prevCommentObj) => {
  if (commentObjsArray.length < 1) return [];

  // handle new comments insertion.
  let initialArr = [];
  let remnantArr = commentObjsArray;

  const firstCommentObj = commentObjsArray[0];

  if (!prevCommentObj) {
    initialArr = [getDividerObj(firstCommentObj.instanceId, firstCommentObj.dueTS), firstCommentObj];
  } else if (prevCommentObj.instanceId !== firstCommentObj.instanceId) {
    initialArr = [getDividerObj(firstCommentObj.instanceId, firstCommentObj.dueTS), firstCommentObj];
  } else {
    initialArr = [firstCommentObj];
  }

  remnantArr = commentObjsArray.slice(1, commentObjsArray.length);

  return remnantArr.reduce((prev, curr) => {
    if (curr.instanceId === prev[prev.length - 1]?.instanceId) return [...prev, curr];
    return [...prev, getDividerObj(curr.instanceId, curr.dueTS), curr];
  }, initialArr);
};

const DateDivider = ({ dueTS }) => (
  <StyledBox position="relative" margin="1.75rem 0">
    <StyledDivider />
    <DateContainer>
      <StyledTypography
        variant="body2"
        color={theme.palette.grey.main}
        style={{ fontSize: '0.75rem', lineHeight: '1' }}
      >
        {convertDateToString2(new Date(dueTS))}
      </StyledTypography>
    </DateContainer>
  </StyledBox>
);

const DateContainer = styled.div`
  left: 50%;
  top: 50%;
  display: inline-block;
  position: absolute;
  border-radius: 1rem;
  background-color: ${(props) => props.theme.palette.borders.lighterGrey};
  transform: translate(-50%, -50%);
  padding: 2px 1.25rem;
`;

const TalkingPointComments = ({
  taskId,
  talkingPoint,
  // scrollToComments,
  // closePanel,
  styleType,
  isMeetingNotesQuestion = false,
  setTalkingQuestionComments,
  comments,
  parentTaskId,
  startTS,
  endTS,
  scrollType = SCROLL_DIRECTION.UPWARD,
  isDisabled,
}) => {
  const [loadingUpward, setLoadingUpward] = useState(false);

  const dispatch = useDispatch();

  const containerRef = useRef(null);
  const dummyRef = useRef(null);

  const { page: upwardPage, hasMore: upwardHasMore, updatePagination: updateUpwardPagination } = usePagination();

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId, shallowEqual);

  const { uuid, visibility } = talkingPoint;

  const loadComments = (limit = LIMIT) => {
    const offset = upwardPage * limit;

    if (loadingUpward) return;
    setLoadingUpward(true);
    return getTalkingPointContinuousComments({
      checkInId: taskId,
      parentId: parentTaskId,
      questionId: uuid,
      limit,
      offset,
      scrollType,
      startTS,
      endTS,
    })
      .then((commentObjs) => {
        updateUpwardPagination({
          hasMore: commentObjs.length === limit,
          page: upwardPage + 1,
        });

        const topMostComment = comments.length > 0 ? comments[0] : null;

        const modifiedComments = addDateDividers(commentObjs.reverse(), null);

        if (
          topMostComment !== null &&
          topMostComment.comment === null &&
          modifiedComments.length > 0 &&
          modifiedComments[modifiedComments.length - 1].instanceId === topMostComment.instanceId
        ) {
          setTalkingQuestionComments((state) => [...modifiedComments, ...state.slice(1, state.length)]);
        } else setTalkingQuestionComments((state) => [...modifiedComments, ...state]);
        setLoadingUpward(false);
      })
      .catch(() => {
        dispatch(showErrorSnackbar('Sorry an error occured while fetching the task comments'));
      })
      .finally(() => {
        setLoadingUpward(false);
      });
  };

  const updateLocalAndFeedComment = useCallback(
    ({ commentId, comment }) => {
      setTalkingQuestionComments((comments) =>
        comments.map((c) => (c.comment?.id === commentId ? { ...c, comment: { ...comment } } : c))
      );
      // on update we get only part of the feed that is post and hence we don't need
      // to normalize it before adding it to the store
      dispatch(
        updateFeedTalkingPointCommentAction({
          commentId,
          comment,
          talkingPointQuestionId: talkingPoint.uuid,
        })
      );
    },
    [dispatch]
  );

  const loadMoreUpward = () => loadComments(UP_LIMIT);

  useEffect(() => {
    if (taskId) {
      updateUpwardPagination({ hasMore: true, page: 0 });
      loadComments(UP_LIMIT);
    }
    return () => setTalkingQuestionComments([]);
  }, [taskId]);

  useEffect(() => {
    if (startTS && endTS && uuid) {
      loadComments();
    }
  }, [startTS, endTS, uuid]);

  useEffect(() => {
    if (dummyRef?.current) {
      if (upwardPage < 2 && comments.filter((x) => x.comment !== null).length <= UP_LIMIT) {
        dummyRef.current.scrollIntoView();
      }
    }
  }, [upwardPage, dummyRef, comments]);
  // return loading ? (
  //   <FeedLoadingView />
  // ) : (
  return (
    <Container ref={containerRef}>
      <InfiniteScroll
        threshold={1}
        isReverse
        pageStart={0}
        hasMore={upwardHasMore}
        loadMore={loadMoreUpward}
        useWindow={false}
        getScrollParent={() => containerRef.current}
        loader={
          <div>
            <Skeleton width="80%" />
            <Skeleton width="80%" />
          </div>
        } // if we didn't use loading, feed loader was always visible when 1st page data didn't occupy the whole page
      >
        {comments &&
          comments.map(({ dueTS, comment }, index) => {
            if (comment === null) {
              return <DateDivider dueTS={dueTS} />;
            }

            return (
              <CommentContainer
                placeholder={isMeetingNotesQuestion ? CHECK_IN_MEETING_NOTES_PLACEHOLDER : CHECK_IN_COMMENT_PLACEHOLDER}
                key={`${comment.id}-taskComment`}
                {...comment}
                updateFeedComment={updateLocalAndFeedComment}
                feedId={taskId}
                feedType="TASK"
                displayReply={false}
                styleType={styleType}
                employeeId={loggedInEmployeeId}
                renderDisplayCommentView={({ subType, ...props }) =>
                  subType === Post.SUB_TYPES.automatedComment ? (
                    <AutomatedComment {...props} />
                  ) : (
                    <SidepanelCommentDisplay {...props} isDisabled={isDisabled} />
                  )
                }
                talkingPointQuestionId={talkingPoint.uuid}
                visibilityChangeDenyTooltip="Visibility change not allowed for private talking point"
                useTeamIcon
                allowVisibilityEdit={visibility !== 'PRIVATE'}
                isDisabled={isDisabled}
              />
            );
          })}

        <div ref={dummyRef} />
      </InfiniteScroll>
      {comments && comments.length === 0 && !loadingUpward && (
        <NoCommentsView
          isCheckIn={!isMeetingNotesQuestion}
          isMeetingNotes={isMeetingNotesQuestion}
          isDisabled={isDisabled}
          firstText="There are no comments"
          secondText="for this talking point"
        />
      )}
    </Container>
  );
  // );
};

const Container = styled.div`
  .automatedComment + .automatedComment {
    margin-bottom: -0.5rem;
  }
  position: relative;
  overflow-y: auto;
  height: 100%;
  padding-right: 1rem;
  // scrollbar-width: none;
  // &::-webkit-scrollbar {
  //   display: none;
  // }
`;

const CommentContainerWithRef = forwardRef(({ children, fetchData }, chatBoxRef) => {
  const { ref, inView, entry } = useInView({
    initialInView: false,
    root: chatBoxRef?.current,
    threshold: 0.75,
  });

  const [usedOnce, setUsedOnce] = useState(false);

  useEffect(() => {
    if (inView && !usedOnce) {
      setUsedOnce(true);
      fetchData();
    }
  }, [usedOnce, inView, fetchData, entry]);

  return <div ref={ref}>{children}</div>;
});

export default TalkingPointComments;
